import { useState, useCallback, useRef } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";

// @material-ui/core components
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import LinearProgress from "@material-ui/core/LinearProgress";
import Link from "@material-ui/core/Link";
import { makeStyles } from "@material-ui/core/styles";
import useLogs from "hooks/logs";

// Karsten
import UploadFile from "components/Dropzone/UploadFile";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import Api from "lib/api/httpRequestHelper";
import UtilsFncs from "utils/UtilsFncs";

const useStyles = makeStyles(() => ({
  defaultCSV: {
    marginTop: "1rem",
    textDecoration: "underline",
  },
}));

const validator = (file) => {
  if (!file.name.includes(".csv")) {
    return {
      code: "invalid-extension",
      message: "A extensão do arquivo deve ser .csv",
    };
  } else if (file.size > 2097152) {
    return {
      code: "invalid-size",
      message: "O tamanho do arquivo não deve ser maior que 2Mb",
    };
  }
};

const getFilesErrors = (files) => {
  const errorsList = files.map((file) => `${file.name}: ${file.error}`);

  return errorsList.map((msg, index) => {
    return (
      <div key={index} style={{ marginBottom: "0.5rem" }}>
        &#8226; <Typography variant="caption">{msg}</Typography>
      </div>
    );
  });
};

function DialogUploadEstoque({ open, onClose, isMobile }) {
  const [importando, setImportando] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const classes = useStyles();
  const snackbar = useSnackbar();
  const contentRef = useRef(null);
  const { setLocalStorageApiProblem } = useLogs();

  const handleClose = useCallback(() => {
    setFilesToUpload([]);
    onClose();
  }, [onClose]);

  const importar = useCallback(() => {
    setImportando(true);

    const uploadPromises = filesToUpload.map((file) => {
      return new Promise((resolve) => {
        UtilsFncs.FileToB64(file)
          .then((base64Csv) => {
            let encodedBase64 = base64Csv
              ?.toString()
              ?.replace(/^data:(.*,)?/, "");

            if (encodedBase64.length % 4 > 0) {
              encodedBase64 += "="?.repeat(4 - (encodedBase64.length % 4));
            }

            Api.post(
              "set-update-stock-csv",
              "stock",
              { base64Csv: encodedBase64 },
              true
            )
              .then((res) => {
                if (res.Success) {
                  resolve({
                    name: file.name,
                    success: true,
                  });
                }
              })
              .catch((error) => {
                resolve({
                  name: file.name,
                  error,
                });
                setLocalStorageApiProblem({
                  idMenuAccess: "109",
                  screen: "Consulta Estoque",
                  problem: `Erro na chamada do endpoint set-update-stock-csv - ${error}`,
                });
              });
          })
          .catch((error) => {
            console.log(
              "Erro ao converter arquivo do upload para b64: " + error
            );
            resolve({
              name: file.name,
              error: "Não foi possível utilizar o arquivo formatado",
            });
          });
      });
    });

    Promise.all(uploadPromises).then((returns) => {
      const filesWithError = returns.filter((file) => Boolean(file.error));

      if (
        filesWithError.length >= 1 &&
        filesWithError.length < returns.length
      ) {
        snackbar.showMessage(
          <div>
            Alguns relatórios retornaram erro, os demais foram bem sucedidos:
            <br />
            <br />
            {getFilesErrors(filesWithError)}
          </div>,
          {
            severity: "warning",
            autoHideDuration: 10000,
          }
        );
      } else if (filesWithError.length === 1) {
        snackbar.showMessage(filesWithError[0].error, {
          severity: "error",
        });
      } else if (filesWithError.length > 1) {
        snackbar.showMessage(
          <div>
            Os seguintes relatórios retornaram erro:
            <br />
            <br />
            {getFilesErrors(filesWithError)}
          </div>,
          {
            severity: "error",
            autoHideDuration: 10000,
          }
        );
      } else {
        snackbar.showMessage("Importação realizada com sucesso!", {
          severity: "success",
        });
        handleClose();
      }

      setImportando(false);
    });
  }, [filesToUpload, snackbar, handleClose]);

  const getCSVDefaultFile = useCallback((e) => {
    e.preventDefault();

    window.URL = window.webkitURL || window.URL;

    const contentType = "text/csv";
    const csvContent = "CNPJ;LocalEstoque;EanProduto;Quantidade";

    const csvFile = new Blob([csvContent], { type: contentType });

    const link = document.createElement("a");
    link.download = "ModeloEstoque.csv";
    link.href = window.URL.createObjectURL(csvFile);

    link.dataset.downloadurl = [contentType, link.download, link.href].join(
      ":"
    );

    document.body.appendChild(link);

    link.click();
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        <span>
          <Typography variant="h3" component="h3">
            Importar Estoque
          </Typography>
        </span>
      </DialogTitle>
      <PerfectScrollbar containerRef={() => contentRef}>
        <DialogContent
          ref={contentRef}
          style={{ overflowY: "hidden", padding: "0 1.5rem" }}
        >
          <Typography id="alert-dialog-description" variant="h4">
            Siga as instruções para a importação do relatório:
          </Typography>
          <Typography variant="caption">
            1) Os cabeçalhos "CNPJ", "LocalEstoque", "EanProduto", "Quantidade"
            são obrigatórios e devem ser escritos exatamente desta forma.
          </Typography>
          <br />
          <Typography variant="caption">
            2) O arquivo deve ter no máximo 2Mb.
          </Typography>
          <br />
          <Typography variant="caption">
            3) A extensão do arquivo deve ser .csv.
          </Typography>
          <br />
          <br />
          <Link
            href="#"
            variant="caption"
            className={classes.defaultCSV}
            onClick={getCSVDefaultFile}
            color="inherit"
          >
            Baixar aqui modelo
          </Link>
          <br />
          <br />
          <UploadFile
            accept=".csv"
            validator={validator}
            onChangeFiles={setFilesToUpload}
            disabled={importando}
          />
          {importando && (
            <>
              <br />
              <div>
                <span>importando relatório...</span>
                <LinearProgress />
              </div>
            </>
          )}
        </DialogContent>
      </PerfectScrollbar>
      <DialogActions
        style={{ display: "flex", justifyContent: isMobile && "center" }}
      >
        <Button component={Box} onClick={handleClose} disabled={importando}>
          Cancelar
        </Button>
        <Button
          component={Box}
          variant="contained"
          onClick={importar}
          disabled={importando}
          color="default"
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DialogUploadEstoque;
